import React, {useEffect} from 'react'
import {IntlProvider, useIntl, FormattedMessage} from 'react-intl'
import {useLocation} from 'react-router-dom'
import {Helmet} from 'react-helmet'
import {useQuery} from '@tanstack/react-query'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {DEFAULT_LOCALE} from '@salesforce/retail-react-app/app/constants'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {getTargetLocale, fetchTranslations, normalizeLocaleForIntl} from '_utils'
import {Box, VStack, Image, Text, useMultiStyleConfig, StackProps, TextProps} from '@chakra-ui/react'
import {useAnalytics, useHeaderSize, useCurrentBasket} from '_customHooks'

import {Footer, Header} from '_uiComponents'
import {DrawerProvider, ModalProvider, LoaderProvider, GlobalConfigProvider} from '_context'
import {CookiesProvider} from 'react-cookie'

interface ErrorProps {
    message?: string
    stack?: string
    status?: number
}

const ErrorSeo = ({status}: {status?: number}) => {
    const {formatMessage} = useIntl()
    const isNotFound = status === 404

    return (
        <Helmet>
            <title>{formatMessage({id: 'global.storename', defaultMessage: 'Brunello Cucinelli - official boutique'})}</title>
            <meta name="description" content={formatMessage({id: 'global.storename', defaultMessage: 'Brunello Cucinelli - official boutique'})} />
            {isNotFound && <meta name="robots" content="noindex, nofollow" />}
        </Helmet>
    )
}

const Error: React.FC<ErrorProps> = ({message, stack, status}) => {
    if (!status) {
        status = 504
    }

    let isFirstLoad = false
    const location = useLocation()
    const {site, locale} = useMultiSite()
    const appOrigin = useAppOrigin()
    const {headerSize} = useHeaderSize()
    const {data: basket} = useCurrentBasket()
    const {analyticsEventInit, analyticsEventPwaPage} = useAnalytics()
    const styles = useMultiStyleConfig('ErrorPage', {headerSize}) as Record<string, StackProps & TextProps>

    const targetLocale = getTargetLocale({
        getUserPreferredLocales: (): string[] => {
            return [locale?.id || DEFAULT_LOCALE]
        },
        l10nConfig: site.l10n,
    })
    const intlLocale = normalizeLocaleForIntl(targetLocale)

    const is404ForMissingTranslationFile = /\/static\/translations\/compiled\/[^.]+\.json$/.test(location?.pathname || '')

    const {data: messages} = useQuery<Record<string, string>>({
        queryKey: ['app', 'translations', 'messages', targetLocale],
        queryFn: async () => {
            if (is404ForMissingTranslationFile) {
                return {}
            }
            return await fetchTranslations(targetLocale, appOrigin)
        },
        enabled: true,
    })

    useEffect(() => {
        if (locale && basket && !isFirstLoad) {
            analyticsEventInit({
                country: locale.id.split('-')[1],
                currency: basket.currency,
                pageCategory: '',
                pageSubCategory: '',
                pageDetail: '',
                pageLanguage: locale.id.split('-')[0],
                pageStatus: String(status),
                listingType: '',
            })
            isFirstLoad = true
        }
    }, [basket])

    useEffect(() => {
        if (window.dataLayer) {
            analyticsEventPwaPage({
                pageCategory: '',
                pagesubCategory: '',
                pageDetail: '',
                pageLanguage: locale.id.split('-')[0],
                pageStatus: String(status),
                listingType: '',
            })
        }
    }, [])

    return (
        <IntlProvider
            onError={(err) => {
                if (!messages) {
                    return
                }
                if (String(err.code) === 'MISSING_TRANSLATION') {
                    console.warn('Missing translation', err.message)
                    return
                }
                throw err
            }}
            locale={intlLocale}
            messages={messages || {}}
            defaultLocale={DEFAULT_LOCALE}
        >
            <CookiesProvider>
                <LoaderProvider>
                    <GlobalConfigProvider>
                        <ModalProvider>
                            <DrawerProvider>
                                <Box id="sf-app" className="error-page">
                                    <ErrorSeo status={status} />
                                    <Header />
                                    <VStack as="main" id="app-main" role="main" layerStyle="page" {...styles.container}>
                                        {status === 404 ? (
                                            <>
                                                <Image {...styles.image} src={`${getAssetUrl('./static/img/404.jpg')}`} />
                                                <Text as="span" {...styles.quote}>
                                                    <FormattedMessage id="error.404.citation" defaultMessage="«The unexamined life is not worth living»" />{' '}
                                                    <br /> <FormattedMessage id="error.404.citation.author" defaultMessage="Socrates" />
                                                </Text>
                                                <Text {...styles.message}>
                                                    <FormattedMessage id="error.404.description" defaultMessage="Sorry, this page has not been found." />
                                                </Text>
                                            </>
                                        ) : (
                                            <>
                                                <Text as="h1" {...styles.errorHeader}>
                                                    500
                                                </Text>
                                                <Text as="span" {...styles.quote}>
                                                    <FormattedMessage
                                                        id="error.500.citation"
                                                        defaultMessage="«A person who never made a mistake never tried anything new»"
                                                    />{' '}
                                                    <br /> <FormattedMessage id="error.500.citation.author" defaultMessage="Albert Einstein" />
                                                </Text>
                                                <Text {...styles.message}>
                                                    <FormattedMessage id="error.500.description" defaultMessage="An error occurred. Our apologies." />
                                                </Text>
                                            </>
                                        )}
                                        {stack && (
                                            <Box marginTop={20}>
                                                <Text fontWeight="bold" fontSize="md">
                                                    {message}
                                                </Text>
                                                <Text fontWeight="bold" fontSize="md">
                                                    Stack Trace
                                                </Text>
                                                <Box
                                                    as="pre"
                                                    mt={4}
                                                    fontSize="sm"
                                                    background="gray.50"
                                                    borderColor="gray.200"
                                                    borderStyle="solid"
                                                    borderWidth="1px"
                                                    overflow="auto"
                                                    padding={4}
                                                >
                                                    {stack}
                                                </Box>
                                            </Box>
                                        )}
                                    </VStack>
                                    <Footer />
                                </Box>
                            </DrawerProvider>
                        </ModalProvider>
                    </GlobalConfigProvider>
                </LoaderProvider>
            </CookiesProvider>
        </IntlProvider>
    )
}

export default Error
